import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

import { useWeb3React } from "@web3-react/core";
import useSWR from "swr";
import { ethers } from "ethers";

import Tab from "../Tab/Tab";
import cx from "classnames";

import { getToken, getTokens, getWhitelistedTokens, getWrappedToken, getNativeToken } from "../../data/Tokens";
import { getContract } from "../../Addresses";
import {
  helperToast,
  useLocalStorageByChainId,
  getTokenInfo,
  // getChainName,
  useChainId,
  expandDecimals,
  fetcher,
  bigNumberify,
  formatAmount,
  formatAmountFree,
  formatKeyAmount,
  // formatDateTime,
  getBuyEquityToAmount,
  getBuyEquityFromAmount,
  getSellEquityFromAmount,
  getSellEquityToAmount,
  parseValue,
  approveTokens,
  getUsd,
  adjustForDecimals,
  EQUITY_DECIMALS,
  USD_DECIMALS,
  BASIS_POINTS_DIVISOR,
  EQUITY_COOLDOWN_DURATION,
  SECONDS_PER_YEAR,
  USDE_DECIMALS,
  FTM,
  PLACEHOLDER_ACCOUNT,
  EQUALEQUITY_DISPLAY_DECIMALS,
} from "../../Helpers";

import { callContract, useEqualPrice, useInfoTokens } from "../../Api";

import TokenSelector from "../Exchange/TokenSelector";
import BuyInputSection from "../BuyInputSection/BuyInputSection";
import Tooltip from "../Tooltip/Tooltip";

import Reader from "../../abis/Reader.json";
import RewardReader from "../../abis/RewardReader.json";
import Vault from "../../abis/Vault.json";
import EquityManager from "../../abis/EquityManager.json";
import RewardTracker from "../../abis/RewardTracker.json";
import Vester from "../../abis/Vester.json";
import RewardRouter from "../../abis/RewardRouter.json";
import Token from "../../abis/Token.json";

import equity24Icon from "../../img/logo-equity.png";
import equity40Icon from "../../img/logo-equity.png";
import arrowIcon from "../../img/ic_convert_down.svg";

import fantom16Icon from "../../img/logo-fantom.png";

import "./EquitySwap.css";
import AssetDropdown from "../../views/Dashboard/AssetDropdown";

const { AddressZero } = ethers.constants;

function getStakingData(stakingInfo) {
  if (!stakingInfo || stakingInfo.length === 0) {
    return;
  }

  const keys = ["stakedEquityTracker", "feeEquityTracker"];
  const data = {};
  const propsLength = 5;

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    data[key] = {
      claimable: stakingInfo[i * propsLength],
      tokensPerInterval: stakingInfo[i * propsLength + 1],
      averageStakedAmounts: stakingInfo[i * propsLength + 2],
      cumulativeRewards: stakingInfo[i * propsLength + 3],
      totalSupply: stakingInfo[i * propsLength + 4],
    };
  }

  return data;
}

export default function EquitySwap(props) {
  const { savedSlippageAmount, isBuying, setPendingTxns, connectWallet, setIsBuying } = props;
  const history = useHistory();
  const swapLabel = isBuying ? "Mint" : "Redeem";
  const tabLabel = isBuying ? "Mint" : "Redeem";
  const { active, library, account } = useWeb3React();
  const { chainId } = useChainId();
  // const chainName = getChainName(chainId)
  const tokens = getTokens(chainId);
  const whitelistedTokens = getWhitelistedTokens(chainId);
  const tokenList = whitelistedTokens.filter((t) => !t.isWrapped);
  const [swapValue, setSwapValue] = useState("");
  const [equityValue, setEquityValue] = useState("");
  const [swapTokenAddress, setSwapTokenAddress] = useLocalStorageByChainId(
    chainId,
    `${swapLabel}-swap-token-address`,
    AddressZero
  );
  const [isApproving, setIsApproving] = useState(false);
  const [isApproving2, setIsApproving2] = useState(false);
  const [isWaitingForApproval, setIsWaitingForApproval] = useState(false);
  const [isWaitingForApproval2, setIsWaitingForApproval2] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [anchorOnSwapAmount, setAnchorOnSwapAmount] = useState(true);
  const [feeBasisPoints, setFeeBasisPoints] = useState("");

  const readerAddress = getContract(chainId, "Reader");
  const rewardReaderAddress = getContract(chainId, "RewardReader");
  const vaultAddress = getContract(chainId, "Vault");
  const nativeTokenAddress = getContract(chainId, "NATIVE_TOKEN");
  const stakedEquityTrackerAddress = getContract(chainId, "StakedEquityTracker");
  const feeEquityTrackerAddress = getContract(chainId, "FeeEquityTracker");
  const usdeAddress = getContract(chainId, "USDE");
  const equalAddress = getContract(chainId, "EQUAL");
  const equityAddress = getContract(chainId, "EQUITY");
  const equityManagerAddress = getContract(chainId, "EquityManager");
  const rewardRouterAddress = getContract(chainId, "RewardRouter");
  const tokensForBalanceAndSupplyQuery = [stakedEquityTrackerAddress, usdeAddress];

  const tokenAddresses = tokens.map((token) => token.address);
  const { data: tokenBalances } = useSWR(
    [`EquitySwap:getTokenBalances:${active}`, chainId, readerAddress, "getTokenBalances", account || PLACEHOLDER_ACCOUNT],
    {
      fetcher: fetcher(library, Reader, [tokenAddresses]),
    }
  );

  const { data: balancesAndSupplies } = useSWR(
    [
      `EquitySwap:getTokenBalancesWithSupplies:${active}`,
      chainId,
      readerAddress,
      "getTokenBalancesWithSupplies",
      account || PLACEHOLDER_ACCOUNT,
    ],
    {
      fetcher: fetcher(library, Reader, [tokensForBalanceAndSupplyQuery]),
    }
  );
  const tokensForSupplyQuery = [equalAddress, equityAddress, usdeAddress];
  const { data: totalSupplies } = useSWR(
    [`Dashboard:totalSupplies:${active}`, chainId, readerAddress, "getTokenBalancesWithSupplies", AddressZero],
    {
      fetcher: fetcher(library, Reader, [tokensForSupplyQuery]),
    }
  );

  const { data: aums } = useSWR([`EquitySwap:getAums:${active}`, chainId, equityManagerAddress, "getAums"], {
    fetcher: fetcher(library, EquityManager),
  });

  const { data: totalTokenWeights } = useSWR(
    [`EquitySwap:totalTokenWeights:${active}`, chainId, vaultAddress, "totalTokenWeights"],
    {
      fetcher: fetcher(library, Vault),
    }
  );

  const tokenAllowanceAddress = swapTokenAddress === AddressZero ? nativeTokenAddress : swapTokenAddress;
  const { data: tokenAllowance } = useSWR(
    [active, chainId, tokenAllowanceAddress, "allowance", account || PLACEHOLDER_ACCOUNT, equityManagerAddress],
    {
      fetcher: fetcher(library, Token),
    }
  );

  const { data: tokenAllowanceEquityToken } = useSWR(
    [active, chainId, "0x180DA8bD91406Dd41F18b6200C53dEb553f86180", "allowance", account || PLACEHOLDER_ACCOUNT, "0x855FaC135Ba4D7bc79fdbE084dE255E87FA79237"],
    {
      fetcher: fetcher(library, Token),
    }
  );

  const { data: lastPurchaseTime } = useSWR(
    [`EquitySwap:lastPurchaseTime:${active}`, chainId, equityManagerAddress, "lastAddedAt", account || PLACEHOLDER_ACCOUNT],
    {
      fetcher: fetcher(library, EquityManager),
    }
  );

  // const { data: equityBalance } = useSWR(
  //   [`EquitySwap:equityBalance:${active}`, chainId, feeEquityTrackerAddress, "stakedAmounts", account || PLACEHOLDER_ACCOUNT],
  //   {
  //     fetcher: fetcher(library, RewardTracker),
  //   }
  // );

  const { data: equityBalance } = useSWR(
    [`EquitySwap:equityBalance:${active}`, chainId, "0x180DA8bD91406Dd41F18b6200C53dEb553f86180", "balanceOf", account || PLACEHOLDER_ACCOUNT],
    {
      fetcher: fetcher(library, Token),
    }
  );

  const equityVesterAddress = getContract(chainId, "EquityVester");
  const { data: reservedAmount } = useSWR(
    [`EquitySwap:reservedAmount:${active}`, chainId, equityVesterAddress, "pairAmounts", account || PLACEHOLDER_ACCOUNT],
    {
      fetcher: fetcher(library, Vester),
    }
  );

  const { equalPrice } = useEqualPrice(chainId, { fantom: chainId === FTM ? library : undefined }, active);

  const rewardTrackersForStakingInfo = [stakedEquityTrackerAddress, feeEquityTrackerAddress];
  const { data: stakingInfo } = useSWR(
    [`EquitySwap:stakingInfo:${active}`, chainId, rewardReaderAddress, "getStakingInfo", account || PLACEHOLDER_ACCOUNT],
    {
      fetcher: fetcher(library, RewardReader, [rewardTrackersForStakingInfo]),
    }
  );

  const stakingData = getStakingData(stakingInfo);

  const redemptionTime = lastPurchaseTime ? lastPurchaseTime.add(EQUITY_COOLDOWN_DURATION) : undefined;
  const inCooldownWindow = redemptionTime && parseInt(Date.now() / 1000) < redemptionTime;

  const equitySupply = totalSupplies ? totalSupplies[3] : bigNumberify(0);
  // const equitySupply = balancesAndSupplies ? balancesAndSupplies[1] : bigNumberify(0);
  const usdeSupply = balancesAndSupplies ? balancesAndSupplies[3] : bigNumberify(0);
  // OLD METHOD FOR EQUITY PRICE CALC
  let aum;
  if (aums && aums.length > 0) {
    aum = isBuying ? aums[0] : aums[1];
  }

  // NEW METHOD FOR EQUITY PRICE CAL
  // let aum;
  // if (aums && aums.length > 0) {
  //   aum = aums[0].add(aums[1]).div(2);
  // }

  const equityPrice =
    aum && aum.gt(0) && equitySupply.gt(0)
      ? aum.mul(expandDecimals(1, EQUITY_DECIMALS)).div(equitySupply)
      : expandDecimals(1, USD_DECIMALS);
  let equityBalanceUsd;
  if (equityBalance) {
    equityBalanceUsd = equityBalance.mul(equityPrice).div(expandDecimals(1, EQUITY_DECIMALS));
  }
  const equitySupplyUsd = equitySupply.mul(equityPrice).div(expandDecimals(1, EQUITY_DECIMALS));

  let reserveAmountUsd;
  if (reservedAmount) {
    reserveAmountUsd = reservedAmount.mul(equityPrice).div(expandDecimals(1, EQUITY_DECIMALS));
  }

  const { infoTokens } = useInfoTokens(library, chainId, active, tokenBalances, undefined);
  const swapToken = getToken(chainId, swapTokenAddress);
  const swapTokenInfo = getTokenInfo(infoTokens, swapTokenAddress);

  const swapTokenBalance = swapTokenInfo && swapTokenInfo.balance ? swapTokenInfo.balance : bigNumberify(0);

  const swapAmount = parseValue(swapValue, swapToken && swapToken.decimals);
  const equityAmount = parseValue(equityValue, EQUITY_DECIMALS);

  const needApproval =
    isBuying && swapTokenAddress !== AddressZero && tokenAllowance && swapAmount && swapAmount.gt(tokenAllowance);

  const needApproval2 =
    tokenAllowanceEquityToken && swapAmount && swapAmount.gt(tokenAllowanceEquityToken);

  const swapUsdMin = getUsd(swapAmount, swapTokenAddress, false, infoTokens);
  const equityUsdMax = equityAmount && equityPrice ? equityAmount.mul(equityPrice).div(expandDecimals(1, EQUITY_DECIMALS)) : undefined;

  let isSwapTokenCapReached;
  if (swapTokenInfo.managedUsd && swapTokenInfo.maxUsdeAmount) {
    isSwapTokenCapReached = swapTokenInfo.managedUsd.gt(
      adjustForDecimals(swapTokenInfo.maxUsdeAmount, USDE_DECIMALS, USD_DECIMALS)
    );
  }

  const onSwapValueChange = (e) => {
    setAnchorOnSwapAmount(true);
    setSwapValue(e.target.value);
  };

  const onEquityValueChange = (e) => {
    setAnchorOnSwapAmount(false);
    setEquityValue(e.target.value);
  };

  const onSelectSwapToken = (token) => {
    setSwapTokenAddress(token.address);
    setIsWaitingForApproval(false);
    setIsWaitingForApproval2(false);
  };

  const nativeToken = getTokenInfo(infoTokens, AddressZero);

  let totalApr = bigNumberify(0);

  let feeEquityTrackerAnnualRewardsUsd;
  let feeEquityTrackerApr;
  if (
    stakingData &&
    stakingData.feeEquityTracker &&
    stakingData.feeEquityTracker.tokensPerInterval &&
    nativeToken &&
    nativeToken.minPrice &&
    equitySupplyUsd &&
    equitySupplyUsd.gt(0)
  ) {
    feeEquityTrackerAnnualRewardsUsd = stakingData.feeEquityTracker.tokensPerInterval
      .mul(SECONDS_PER_YEAR)
      .mul(nativeToken.minPrice)
      .div(expandDecimals(1, 18));
    feeEquityTrackerApr = feeEquityTrackerAnnualRewardsUsd.mul(BASIS_POINTS_DIVISOR).div(equitySupplyUsd);
    totalApr = totalApr.add(feeEquityTrackerApr);
  }

  let stakedEquityTrackerAnnualRewardsUsd;
  let stakedEquityTrackerApr;

  if (
    equalPrice &&
    stakingData &&
    stakingData.stakedEquityTracker &&
    stakingData.stakedEquityTracker.tokensPerInterval &&
    equitySupplyUsd &&
    equitySupplyUsd.gt(0)
  ) {
    stakedEquityTrackerAnnualRewardsUsd = stakingData.stakedEquityTracker.tokensPerInterval
      .mul(SECONDS_PER_YEAR)
      .mul(equalPrice)
      .div(expandDecimals(1, 18));
    stakedEquityTrackerApr = stakedEquityTrackerAnnualRewardsUsd.mul(BASIS_POINTS_DIVISOR).div(equitySupplyUsd);
    totalApr = totalApr.add(stakedEquityTrackerApr);
  }

  useEffect(() => {
    const updateSwapAmounts = () => {
      if (anchorOnSwapAmount) {
        if (!swapAmount) {
          setEquityValue("");
          setFeeBasisPoints("");
          return;
        }

        if (isBuying) {
          const { amount: nextAmount, feeBasisPoints: feeBps } = getBuyEquityToAmount(
            swapAmount,
            swapTokenAddress,
            infoTokens,
            equityPrice,
            usdeSupply,
            totalTokenWeights
          );
          const nextValue = formatAmountFree(nextAmount, EQUITY_DECIMALS, EQUITY_DECIMALS);
          setEquityValue(nextValue);
          setFeeBasisPoints(feeBps);
        } else {
          const { amount: nextAmount, feeBasisPoints: feeBps } = getSellEquityFromAmount(
            swapAmount,
            swapTokenAddress,
            infoTokens,
            equityPrice,
            usdeSupply,
            totalTokenWeights
          );
          const nextValue = formatAmountFree(nextAmount, EQUITY_DECIMALS, EQUITY_DECIMALS);
          setEquityValue(nextValue);
          setFeeBasisPoints(feeBps);
        }

        return;
      }

      if (!equityAmount) {
        setSwapValue("");
        setFeeBasisPoints("");
        return;
      }

      if (swapToken) {
        if (isBuying) {
          const { amount: nextAmount, feeBasisPoints: feeBps } = getBuyEquityFromAmount(
            equityAmount,
            swapTokenAddress,
            infoTokens,
            equityPrice,
            usdeSupply,
            totalTokenWeights
          );
          const nextValue = formatAmountFree(nextAmount, swapToken.decimals, swapToken.decimals);
          setSwapValue(nextValue);
          setFeeBasisPoints(feeBps);
        } else {
          const { amount: nextAmount, feeBasisPoints: feeBps } = getSellEquityToAmount(
            equityAmount,
            swapTokenAddress,
            infoTokens,
            equityPrice,
            usdeSupply,
            totalTokenWeights,
            true
          );

          const nextValue = formatAmountFree(nextAmount, swapToken.decimals, swapToken.decimals);
          setSwapValue(nextValue);
          setFeeBasisPoints(feeBps);
        }
      }
    };

    updateSwapAmounts();
  }, [
    isBuying,
    anchorOnSwapAmount,
    swapAmount,
    equityAmount,
    swapToken,
    swapTokenAddress,
    infoTokens,
    equityPrice,
    usdeSupply,
    totalTokenWeights,
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const switchSwapOption = (hash = "") => {
    history.push(`${history.location.pathname}#${hash}`);
    props.setIsBuying(hash === "redeem" ? false : true);
  };

  const fillMaxAmount = () => {
    if (isBuying) {
      setAnchorOnSwapAmount(true);
      setSwapValue(formatAmountFree(swapTokenBalance, swapToken.decimals, swapToken.decimals));
      return;
    }

    setAnchorOnSwapAmount(false);
    setEquityValue(formatAmountFree(maxSellAmount, EQUITY_DECIMALS, EQUITY_DECIMALS));
  };

  const getError = () => {
    if (!isBuying && inCooldownWindow) {
      return [`Redemption time not yet reached`];
    }

    if (!swapAmount || swapAmount.eq(0)) {
      return ["ENTER AN AMOUNT"];
    }
    if (!equityAmount || equityAmount.eq(0)) {
      return ["ENTER AN AMOUNT"];
    }

    if (isBuying) {
      const swapTokenInfo = getTokenInfo(infoTokens, swapTokenAddress);
      if (swapTokenInfo && swapTokenInfo.balance && swapAmount && swapAmount.gt(swapTokenInfo.balance)) {
        return [`INSUFFICIENT ${swapTokenInfo.symbol} BALANCE`];
      }

      if (swapTokenInfo.maxUsdeAmount && swapTokenInfo.usdeAmount && swapUsdMin) {
        const usdeFromAmount = adjustForDecimals(swapUsdMin, USD_DECIMALS, USDE_DECIMALS);
        const nextUsdeAmount = swapTokenInfo.usdeAmount.add(usdeFromAmount);
        if (swapTokenInfo.maxUsdeAmount.gt(0) && nextUsdeAmount.gt(swapTokenInfo.maxUsdeAmount)) {
          return [`${swapTokenInfo.symbol} pool exceeded, try different token`, true];
        }
      }
    }

    if (!isBuying) {
      if (maxSellAmount && equityAmount && equityAmount.gt(maxSellAmount)) {
        return [`INSUFFICIENT EQUITY BALANCE`];
      }

      const swapTokenInfo = getTokenInfo(infoTokens, swapTokenAddress);
      if (
        swapTokenInfo &&
        swapTokenInfo.availableAmount &&
        swapAmount &&
        swapAmount.gt(swapTokenInfo.availableAmount)
      ) {
        return [`INSUFFICIENT LIQUIDITY`];
      }
    }

    return [false];
  };

  const isPrimaryEnabled = () => {
    if (!active) {
      return true;
    }
    const [error, modal] = getError();
    if(error){
      console.error(error);
    }
    if (error && !modal) {
      return false;
    }
    if ((needApproval && isWaitingForApproval) || isApproving) {
      return false;
    }
    if ((needApproval2 && isWaitingForApproval2) || isApproving2) {
      return false;
    }
    if (isApproving) {
      return false;
    }
    if (isApproving2) {
      return false;
    }
    if (isSubmitting) {
      return false;
    }
    if (isSwapTokenCapReached) {
      return false;
    }

    return true;
  };

  const getPrimaryText = () => {
    if (!active) {
      return "Connect Wallet";
    }
    const [error, modal] = getError();
    if(error){
      console.error(error);
    }

    if (error && !modal) {
      return error;
    }
    if (isBuying && isSwapTokenCapReached) {
      return `Max Capacity for ${swapToken.symbol} Reached`;
    }

    if (needApproval && isWaitingForApproval) {
      return "Waiting for Approval";
    }
    if (isApproving) {
      return `Approving ${swapToken.symbol}...`;
    }
    if (needApproval) {
      return `Approve ${swapToken.symbol}`;
    }

    if (needApproval2 && isWaitingForApproval2) {
      return "Waiting for Approval";
    }
    if (isApproving2) {
      return `Approving EQUITY Token...`;
    }
    if (needApproval2) {
      return `Approve EQUITY Token`;
    }

    if (isSubmitting) {
      return isBuying ? `Buying...` : `Selling...`;
    }

    return isBuying ? "Mint" : "Redeem";
  };

  const approveFromToken = () => {
    approveTokens({
      setIsApproving,
      library,
      tokenAddress: swapToken.address,
      spender: equityManagerAddress,
      chainId: chainId,
      onApproveSubmitted: () => {
        setIsWaitingForApproval(true);
      },
      infoTokens,
      getTokenInfo,
    });
  };

  const approveFromToken2 = () => {
    approveTokens({
      setIsApproving: setIsApproving2,
      library,
      tokenAddress: "0x180DA8bD91406Dd41F18b6200C53dEb553f86180",
      spender: "0x855FaC135Ba4D7bc79fdbE084dE255E87FA79237",
      chainId: chainId,
      onApproveSubmitted: () => {
        setIsWaitingForApproval2(true);
      },
      infoTokens,
      getTokenInfo,
    });
  };

  const buyEquity = () => {
    setIsSubmitting(true);

    const minEquity = equityAmount.mul(BASIS_POINTS_DIVISOR - savedSlippageAmount).div(BASIS_POINTS_DIVISOR);

    const contract = new ethers.Contract(rewardRouterAddress, RewardRouter, library.getSigner());
    const method = swapTokenAddress === AddressZero ? "mintAndStakeEquityETH" : "mintAndStakeEquity";
    const params = swapTokenAddress === AddressZero ? [0, minEquity] : [swapTokenAddress, swapAmount, 0, minEquity];
    const value = swapTokenAddress === AddressZero ? swapAmount : 0;

    callContract(chainId, contract, method, params, {
      value,
      sentMsg: "Buy submitted.",
      failMsg: "Buy failed.",
      successMsg: `${formatAmount(equityAmount, 18, 4, true)} EQUITY bought with ${formatAmount(
        swapAmount,
        swapTokenInfo.decimals,
        4,
        true
      )} ${swapTokenInfo.symbol}!`,
      setPendingTxns,
    })
      .then(async () => {})
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const sellEquity = () => {
    setIsSubmitting(true);

    const minOut = swapAmount.mul(BASIS_POINTS_DIVISOR - savedSlippageAmount).div(BASIS_POINTS_DIVISOR);

    const contract = new ethers.Contract(rewardRouterAddress, RewardRouter, library.getSigner());
    const method = swapTokenAddress === AddressZero ? "unstakeAndRedeemEquityETH" : "unstakeAndRedeemEquity";
    const params =
      swapTokenAddress === AddressZero ? [equityAmount, minOut, account] : [swapTokenAddress, equityAmount, minOut, account];

    callContract(chainId, contract, method, params, {
      sentMsg: "Sell submitted!",
      failMsg: "Sell failed.",
      successMsg: `${formatAmount(equityAmount, 18, 4, true)} EQUITY sold for ${formatAmount(
        swapAmount,
        swapTokenInfo.decimals,
        4,
        true
      )} ${swapTokenInfo.symbol}!`,
      setPendingTxns,
    })
      .then(async () => {})
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const onClickPrimary = () => {
    if (!active) {
      connectWallet();
      return;
    }

    if (needApproval2) {
      approveFromToken2();
      return;
    }
    if (needApproval) {
      approveFromToken();
      return;
    }

    const [error, modal] = getError();
    if(error){
      console.error(error);
    }

    if (modal) {
      return;
    }

    if (isBuying) {
      buyEquity();
    } else {
      sellEquity();
    }
  };

  let payLabel = "Pay";
  let receiveLabel = "Receive";
  let payBalance = "$0.00";
  let receiveBalance = "$0.00";
  if (isBuying) {
    if (swapUsdMin) {
      payBalance = `$${formatAmount(swapUsdMin, USD_DECIMALS, 2, true)}`;
    }
    if (equityUsdMax) {
      receiveBalance = `$${formatAmount(equityUsdMax, USD_DECIMALS, 2, true)}`;
    }
  } else {
    if (equityUsdMax) {
      payBalance = `$${formatAmount(equityUsdMax, USD_DECIMALS, 2, true)}`;
    }
    if (swapUsdMin) {
      receiveBalance = `$${formatAmount(swapUsdMin, USD_DECIMALS, 2, true)}`;
    }
  }

  const selectToken = (token) => {
    setAnchorOnSwapAmount(false);
    setSwapTokenAddress(token.address);
    helperToast.success(`${token.symbol} selected in order form`);
  };

  let feePercentageText = formatAmount(feeBasisPoints, 2, 2, true, "-");
  if (feeBasisPoints !== undefined && feeBasisPoints.toString().length > 0) {
    feePercentageText += "%";
  }

  let maxSellAmount = equityBalance;
  if (equityBalance && reservedAmount) {
    maxSellAmount = equityBalance.sub(reservedAmount);
  }

  const wrappedTokenSymbol = getWrappedToken(chainId).symbol;
  const nativeTokenSymbol = getNativeToken(chainId).symbol;

  const onSwapOptionChange = (opt) => {
    if (opt === "Redeem") {
      switchSwapOption("redeem");
    } else {
      switchSwapOption();
    }
  };

  return (
    <div className="EquitySwap">
      {/* <div className="Page-title-section">
        <div className="Page-title">{isBuying ? "Buy EQUITY" : "Sell EQUITY"}</div>
        {isBuying && <div className="Page-description">
          Purchase <a href=" https://docs.equalizer.exchange/tokenomics/equity" target="_blank" rel="noopener noreferrer">EQUITY tokens</a> to earn {nativeTokenSymbol} fees from swaps and leverage trading.<br/>
          Note that there is a minimum holding time of 15 minutes after a purchase.<br/>
          <div>View <Link to="/earn">staking</Link> page.</div>
        </div>}
        {!isBuying && <div className="Page-description">
          Redeem your EQUITY tokens for any supported asset.
          {inCooldownWindow && <div>
            EQUITY tokens can only be redeemed 15 minutes after your most recent purchase.<br/>
            Your last purchase was at {formatDateTime(lastPurchaseTime)}, you can redeem EQUITY tokens after {formatDateTime(redemptionTime)}.<br/>
          </div>}
          <div>View <Link to="/earn">staking</Link> page.</div>
        </div>}
      </div> */}
      <div className="EquitySwap-content">
        <div className="App-card EquitySwap-stats-card">
          <div className="App-card-title">
            <div className="App-card-title-mark">
              <div className="App-card-title-mark-icon">
                <img src={equity40Icon} alt="equity40Icon" style={{width: '40px', height: '40px'}}/>
                  <img src={fantom16Icon} style={{width: '16px', height: '16px'}} alt="fantom16Icon" className="selected-network-symbol" />
              </div>
              <div className="App-card-title-mark-info">
                <div className="App-card-title-mark-title">EQUITY</div>
                <div className="App-card-title-mark-subtitle">EQUITY</div>
              </div>
            </div>
          </div>
          <div className="App-card-divider"></div>
          <div className="App-card-content">
            <div className="App-card-row">
              <div className="label">Price</div>
              <div className="value">${formatAmount(equityPrice, USD_DECIMALS, EQUALEQUITY_DISPLAY_DECIMALS, true)}</div>
            </div>
            <div className="App-card-row">
              <div className="label">Wallet</div>
              <div className="value">
                {formatAmount(equityBalance, EQUITY_DECIMALS, 4, true)} EQUITY ($
                {formatAmount(equityBalanceUsd, USD_DECIMALS, 2, true)})
              </div>
            </div>
          </div>
          <div className="App-card-divider"></div>
          <div className="App-card-content">
            {!isBuying && (
              <div className="App-card-row">
                <div className="label">Reserved</div>
                <div className="value">
                  <Tooltip
                    handle={`${formatAmount(reservedAmount, 18, 4, true)} EQUITY ($${formatAmount(
                      reserveAmountUsd,
                      USD_DECIMALS,
                      2,
                      true
                    )})`}
                    position="right-bottom"
                    renderContent={() =>
                      `${formatAmount(reservedAmount, 18, 4, true)} Reserved EQUITY for vesting.`
                    }
                  />
                </div>
              </div>
            )}
            {/* <div className="App-card-row">
              <div className="label">APR</div>
              <div className="value">
                <Tooltip
                  handle={`${formatAmount(totalApr, 2, 2, true)}%`}
                  position="right-bottom"
                  renderContent={() => {
                    return (
                      <>
                        <div className="Tooltip-row">
                          <span className="label">
                            {nativeTokenSymbol} ({wrappedTokenSymbol}) APR
                          </span>
                          <span>{formatAmount(feeEquityTrackerApr, 2, 2, false)}%</span>
                        </div>
                        <div className="Tooltip-row">
                          <span className="label">Escrowed EQUAL APR</span>
                          <span>{formatAmount(stakedEquityTrackerApr, 2, 2, false)}%</span>
                        </div>
                      </>
                    );
                  }}
                />
              </div>
            </div> */}
            <div className="App-card-row">
              <div className="label">Total Supply</div>
              <div className="value">
                {formatAmount(equitySupply, EQUITY_DECIMALS, 4, true)} EQUITY ($
                {formatAmount(equitySupplyUsd, USD_DECIMALS, 2, true)})
              </div>
            </div>
          </div>
        </div>
        <div className="EquitySwap-box App-box">
          <Tab
            options={["Mint", "Redeem"]}
            option={tabLabel}
            onChange={onSwapOptionChange}
            className="Exchange-swap-option-tabs"
          />
          {isBuying && (
            <BuyInputSection
              topLeftLabel={payLabel}
              topRightLabel={`Balance: `}
              tokenBalance={`${formatAmount(swapTokenBalance, swapToken.decimals, 4, true)}`}
              inputValue={swapValue}
              onInputValueChange={onSwapValueChange}
              showMaxButton={swapValue !== formatAmountFree(swapTokenBalance, swapToken.decimals, swapToken.decimals)}
              onClickTopRightLabel={fillMaxAmount}
              onClickMax={fillMaxAmount}
              selectedToken={swapToken}
              balance={payBalance}
            >
              <TokenSelector
                label="Pay"
                chainId={chainId}
                tokenAddress={swapTokenAddress}
                onSelectToken={onSelectSwapToken}
                tokens={whitelistedTokens}
                infoTokens={infoTokens}
                className="EquitySwap-from-token"
                showSymbolImage={true}
                showTokenImgInDropdown={true}
              />
            </BuyInputSection>
          )}

          {!isBuying && (
            <BuyInputSection
              topLeftLabel={payLabel}
              topRightLabel={`Available: `}
              tokenBalance={`${formatAmount(maxSellAmount, EQUITY_DECIMALS, 4, true)}`}
              inputValue={equityValue}
              onInputValueChange={onEquityValueChange}
              showMaxButton={equityValue !== formatAmountFree(maxSellAmount, EQUITY_DECIMALS, EQUITY_DECIMALS)}
              onClickTopRightLabel={fillMaxAmount}
              onClickMax={fillMaxAmount}
              balance={payBalance}
              defaultTokenName={"EQUITY"}
            >
              <div className="selected-token">
                EQUITY <img src={equity24Icon} style={{width: '24px', height: '24px'}} alt="equity24Icon" />
              </div>
            </BuyInputSection>
          )}

          <div className="AppOrder-ball-container">
            <div className="AppOrder-ball">
              <img
                src={arrowIcon}
                alt="arrowIcon"
                onClick={() => {
                  setIsBuying(!isBuying);
                  switchSwapOption(isBuying ? "redeem" : "");
                }}
              />
            </div>
          </div>

          {isBuying && (
            <BuyInputSection
              topLeftLabel={receiveLabel}
              topRightLabel={`Balance: `}
              tokenBalance={`${formatAmount(equityBalance, EQUITY_DECIMALS, 4, true)}`}
              inputValue={equityValue}
              onInputValueChange={onEquityValueChange}
              balance={receiveBalance}
              defaultTokenName={"EQUITY"}
            >
              <div className="selected-token">
                EQUITY <img src={equity24Icon} style={{width: '24px', height: '24px'}} alt="equity24Icon" />
              </div>
            </BuyInputSection>
          )}

          {!isBuying && (
            <BuyInputSection
              topLeftLabel={receiveLabel}
              topRightLabel={`Balance: `}
              tokenBalance={`${formatAmount(swapTokenBalance, swapToken.decimals, 4, true)}`}
              inputValue={swapValue}
              onInputValueChange={onSwapValueChange}
              balance={receiveBalance}
              selectedToken={swapToken}
            >
              <TokenSelector
                label="Receive"
                chainId={chainId}
                tokenAddress={swapTokenAddress}
                onSelectToken={onSelectSwapToken}
                tokens={whitelistedTokens}
                infoTokens={infoTokens}
                className="EquitySwap-from-token"
                showSymbolImage={true}
                showTokenImgInDropdown={true}
              />
            </BuyInputSection>
          )}
          <div>
            <div className="Exchange-info-row">
              <div className="Exchange-info-label">{feeBasisPoints > 50 ? "WARNING: High Fees" : "Fees"}</div>
              <div className="align-right fee-block">
                {isBuying && (
                  <Tooltip
                    handle={isBuying && isSwapTokenCapReached ? "NA" : feePercentageText}
                    position="right-bottom"
                    renderContent={() => {
                      return (
                        <>
                          {feeBasisPoints > 50 && <div>Select an alternative asset for providing liquidity to reduce fees.</div>}
                          To get the lowest fee percentages, look in the "SAVE FEES" section below.
                        </>
                      );
                    }}
                  />
                )}
                {!isBuying && (
                  <Tooltip
                    handle={feePercentageText}
                    position="right-bottom"
                    renderContent={() => {
                      return (
                        <>
                          {feeBasisPoints > 50 && <div>To reduce fees, select a different asset to remove liquidity.</div>}
                          To get the lowest fee percentages, look in the "SAVE FEES" section below.
                        </>
                      );
                    }}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="EquitySwap-cta Exchange-swap-button-container">
            <button className="App-cta Exchange-swap-button text-uppercase" onClick={onClickPrimary} disabled={!isPrimaryEnabled()}>
              {getPrimaryText()}
            </button>
          </div>
        </div>
      </div>
      <div className="Tab-title-section" style={{marginLeft:-12}}>
        <div className="Page-title">SAVE FEES</div>
        {isBuying && (
          <div className="Page-description">
           The fees can vary depending on the asset you want to use to add liquidity to EQUITY.
            <br /> Please enter the desired amount of EQUITY or asset to be added to the interface, and you can compare the fees here.
          </div>
        )}
        {!isBuying && (
          <div className="Page-description">
                     The fees can vary depending on the asset you want to use to add liquidity to EQUITY.
            <br /> Please enter the desired amount of EQUITY or asset to be added to the interface, and you can compare the fees here.
          </div>
        )}
      </div>
      <div className="EquitySwap-token-list">
        {/* <div className="EquitySwap-token-list-content"> */}
        <table className="token-table">
          <thead>
            <tr>
              <th>TOKEN</th>
              <th>PRICE</th>
              <th>
                {isBuying ? (
                  <Tooltip
                    handle={"AVAILABLE"}
                    tooltipIconPosition="right"
                    position="right-bottom text-none"
                    renderContent={() => "Available amount to deposit into EQUITY."}
                  />
                ) : (
                  <Tooltip
                    handle={"AVAILABLE"}
                    tooltipIconPosition="right"
                    position="right-bottom text-none"
                    renderContent={() => {
                      return (
                        <>
                          <div>Available amount to -LIQ. from EQUITY.</div>
                          <div>Funds that are not being utilized by current open positions.</div>
                        </>
                      );
                    }}
                  />
                )}
              </th>
              <th>WALLET</th>
              <th>
                <Tooltip
                  handle={"FEES"}
                  tooltipIconPosition="right"
                  position="right-bottom text-none"
                  renderContent={() => {
                    return (
                      <>
                        <div>Fees will be shown once you have entered an amount in the order form.</div>
                      </>
                    );
                  }}
                />
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {tokenList.map((token) => {
              let tokenFeeBps;
              if (isBuying) {
                const { feeBasisPoints: feeBps } = getBuyEquityFromAmount(
                  equityAmount,
                  token.address,
                  infoTokens,
                  equityPrice,
                  usdeSupply,
                  totalTokenWeights
                );
                tokenFeeBps = feeBps;
              } else {
                const { feeBasisPoints: feeBps } = getSellEquityToAmount(
                  equityAmount,
                  token.address,
                  infoTokens,
                  equityPrice,
                  usdeSupply,
                  totalTokenWeights
                );
                tokenFeeBps = feeBps;
              }
              const tokenInfo = getTokenInfo(infoTokens, token.address);
              let managedUsd;
              if (tokenInfo && tokenInfo.managedUsd) {
                managedUsd = tokenInfo.managedUsd;
              }
              let availableAmountUsd;
              if (tokenInfo && tokenInfo.minPrice && tokenInfo.availableAmount) {
                availableAmountUsd = tokenInfo.availableAmount
                  .mul(tokenInfo.minPrice)
                  .div(expandDecimals(1, token.decimals));
              }
              let balanceUsd;
              if (tokenInfo && tokenInfo.minPrice && tokenInfo.balance) {
                balanceUsd = tokenInfo.balance.mul(tokenInfo.minPrice).div(expandDecimals(1, token.decimals));
              }

              var tokenImage = null;

              try {
                if (token.symbol.toLowerCase() === "ftm" || token.symbol.toLowerCase() === "wftm") {
                  tokenImage = require("../../img/logo-fantom.png");
                } else {
                  tokenImage = require("../../img/ic_" + token.symbol.toLowerCase() + "_40.svg");
                }
              } catch (error) {
                console.error(error);
              }
              let isCapReached = tokenInfo.managedAmount?.gt(tokenInfo.maxUsdeAmount);

              let amountLeftToDeposit;
              if (tokenInfo.maxUsdeAmount && tokenInfo.maxUsdeAmount.gt(0)) {
                amountLeftToDeposit = adjustForDecimals(tokenInfo.maxUsdeAmount, USDE_DECIMALS, USD_DECIMALS).sub(
                  tokenInfo.managedUsd
                );
              }
              function renderFees() {
                const swapUrl = `https://equalizer.exchange/swap`;
                // const swapUrl = `https://equalizer.exchange/#/swap?inputCurrency=${token.address}`;
                switch (true) {
                  case (isBuying && isCapReached) || (!isBuying && managedUsd?.lt(1)):
                    return (
                      <Tooltip
                        handle="NA"
                        position="right-bottom"
                        renderContent={() => (
                          <div>
                            Max pool capacity reached for {tokenInfo.symbol}
                            <br />
                            <br />
                            Please mint EQUITY using another token
                            <br />
                            <p>
                              <a href={swapUrl} target="_blank" rel="noreferrer">
                                Swap on {chainId === FTM ? "Equalizer" : "Equalizer"}
                              </a>
                            </p>
                          </div>
                        )}
                      />
                    );
                  case (isBuying && !isCapReached) || (!isBuying && managedUsd?.gt(0)):
                    return `${formatAmount(tokenFeeBps, 2, 2, true, "-")}${
                      tokenFeeBps !== undefined && tokenFeeBps.toString().length > 0 ? "%" : ""
                    }`;
                  default:
                    return "";
                }
              }

              return (
                <tr key={token.symbol}>
                  <td>
                    <div className="App-card-title-info">
                      <div className="App-card-title-info-icon">
                        <img src={tokenImage && tokenImage.default} alt={token.symbol} width="40px" />
                      </div>
                      <div className="App-card-title-info-text">
                        <div className="App-card-info-title">{token.name}</div>
                        <div className="App-card-info-subtitle">{token.symbol}</div>
                      </div>
                      <div>
                        <AssetDropdown assetSymbol={token.symbol} assetInfo={token} />
                      </div>
                    </div>
                  </td>
                  <td>${formatKeyAmount(tokenInfo, "maxPrimaryPrice", USD_DECIMALS, tokenInfo.displayDecimals, true)}
                  </td>
                  {/* <td>${(tokenInfo.symbol === "USDC" || tokenInfo.symbol === "DAI" || tokenInfo.symbol === "USDT")
                    ? 
                    Math.round(formatKeyAmount(tokenInfo, "maxPrimaryPrice", USD_DECIMALS, 2, true)).toFixed(2) 
                    : formatKeyAmount(tokenInfo, "maxPrimaryPrice", USD_DECIMALS, tokenInfo.displayDecimals, true)}
                  </td> */}
                  <td>
                    {isBuying && (
                      <div>
                        <Tooltip
                          handle={
                            amountLeftToDeposit && amountLeftToDeposit.lt(0)
                              ? "$0.00"
                              : `$${formatAmount(amountLeftToDeposit, USD_DECIMALS, 2, true)}`
                          }
                          position="right-bottom"
                          tooltipIconPosition="right"
                          renderContent={() => {
                            return (
                              <>
                                Current Pool Amount: ${formatAmount(managedUsd, USD_DECIMALS, 2, true)} (
                                {formatKeyAmount(tokenInfo, "poolAmount", token.decimals, 2, true)} {token.symbol})
                                <br />
                                <br />
                                Max Pool Capacity: ${formatAmount(tokenInfo.maxUsdeAmount, 18, 0, true)}
                              </>
                            );
                          }}
                        />
                      </div>
                    )}
                    {!isBuying && (
                      <div>
                        {formatKeyAmount(tokenInfo, "availableAmount", token.decimals, 2, true)} {token.symbol} ($
                        {formatAmount(availableAmountUsd, USD_DECIMALS, 2, true)})
                      </div>
                    )}
                  </td>
                  <td>
                    {formatKeyAmount(tokenInfo, "balance", tokenInfo.decimals, 2, true)} {tokenInfo.symbol} ($
                    {formatAmount(balanceUsd, USD_DECIMALS, 2, true)})
                  </td>
                  <td>{renderFees()}</td>
                  <td>
                    <button
                      className={cx("App-button-option action-btn", isBuying ? "buying" : "selling")}
                      onClick={() => selectToken(token)}
                    >
                      {isBuying ? "Mint with " + token.symbol : "Withdraw as " + token.symbol}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="token-grid">
          {tokenList.map((token) => {
            let tokenFeeBps;
            if (isBuying) {
              const { feeBasisPoints: feeBps } = getBuyEquityFromAmount(
                equityAmount,
                token.address,
                infoTokens,
                equityPrice,
                usdeSupply,
                totalTokenWeights
              );
              tokenFeeBps = feeBps;
            } else {
              const { feeBasisPoints: feeBps } = getSellEquityToAmount(
                equityAmount,
                token.address,
                infoTokens,
                equityPrice,
                usdeSupply,
                totalTokenWeights
              );
              tokenFeeBps = feeBps;
            }
            const tokenInfo = getTokenInfo(infoTokens, token.address);
            let managedUsd;
            if (tokenInfo && tokenInfo.managedUsd) {
              managedUsd = tokenInfo.managedUsd;
            }
            let availableAmountUsd;
            if (tokenInfo && tokenInfo.minPrice && tokenInfo.availableAmount) {
              availableAmountUsd = tokenInfo.availableAmount
                .mul(tokenInfo.minPrice)
                .div(expandDecimals(1, token.decimals));
            }
            let balanceUsd;
            if (tokenInfo && tokenInfo.minPrice && tokenInfo.balance) {
              balanceUsd = tokenInfo.balance.mul(tokenInfo.minPrice).div(expandDecimals(1, token.decimals));
            }

            let amountLeftToDeposit;
            if (tokenInfo.maxUsdeAmount && tokenInfo.maxUsdeAmount.gt(0)) {
              amountLeftToDeposit = adjustForDecimals(tokenInfo.maxUsdeAmount, USDE_DECIMALS, USD_DECIMALS).sub(
                tokenInfo.managedUsd
              );
            }
            let isCapReached = tokenInfo.managedAmount?.gt(tokenInfo.maxUsdeAmount);

            var tokenImage = null;

            try {
              if (token.symbol.toLowerCase() === "ftm" || token.symbol.toLowerCase() === "wftm") {
                tokenImage = require("../../img/logo-fantom.png");
              } else {
                tokenImage = require("../../img/ic_" + token.symbol.toLowerCase() + "_40.svg");
              }
            } catch (error) {
              console.error(error);
            }

            function renderFees() {
              switch (true) {
                case (isBuying && isCapReached) || (!isBuying && managedUsd?.lt(1)):
                  return (
                    <Tooltip
                      handle="NA"
                      position="right-bottom"
                      renderContent={() =>
                        `Maximum pool capacity reached for ${tokenInfo.symbol}. Please add +LIQ. with another token for the EQUITY`
                      }
                    />
                  );
                case (isBuying && !isCapReached) || (!isBuying && managedUsd?.gt(0)):
                  return `${formatAmount(tokenFeeBps, 2, 2, true, "-")}${
                    tokenFeeBps !== undefined && tokenFeeBps.toString().length > 0 ? "%" : ""
                  }`;
                default:
                  return "";
              }
            }

            return (
              <div className="App-card" key={token.symbol}>
                  <img src={tokenImage && tokenImage.default} alt={token.symbol} width="40px" /><span className="mt-2 mx-1">{token.name}</span>
                <div className="App-card-title">{/* {token.name} */}</div>
                <div className="App-card-divider"></div>
                <div className="App-card-content">
                  <div className="App-card-row">
                    <div className="label">Price</div>
                    <div>${formatKeyAmount(tokenInfo, "minPrice", USD_DECIMALS, tokenInfo.displayDecimals, true)}</div>
                  </div>
                  {isBuying && (
                    <div className="App-card-row">
                      <Tooltip
                        className="label"
                        handle="Available"
                        position="left-bottom"
                        renderContent={() => "Available amount to deposit into EQUITY."}
                      />
                      <div>
                        <Tooltip
                          handle={amountLeftToDeposit && `$${formatAmount(amountLeftToDeposit, USD_DECIMALS, 2, true)}`}
                          position="right-bottom"
                          tooltipIconPosition="right"
                          renderContent={() => {
                            return (
                              <>
                                Current Pool Amount: ${formatAmount(managedUsd, USD_DECIMALS, 2, true)} (
                                {formatKeyAmount(tokenInfo, "poolAmount", token.decimals, 2, true)} {token.symbol})
                                <br />
                                <br />
                                Maximum Pool Capacity: ${formatAmount(tokenInfo.maxUsdeAmount, 18, 0, true)}
                              </>
                            );
                          }}
                        />
                      </div>
                    </div>
                  )}
                  {!isBuying && (
                    <div className="App-card-row">
                      <Tooltip
                        handle="Available"
                        position="left-bottom"
                        renderContent={() => {
                          return (
                            <>
                              <div>Available amount to withdraw from EQUITY.</div>
                              <div>Funds not utilized by current open positions.</div>
                            </>
                          );
                        }}
                      />
                      <div>
                        {formatKeyAmount(tokenInfo, "availableAmount", token.decimals, 2, true)} {token.symbol} ($
                        {formatAmount(availableAmountUsd, USD_DECIMALS, 2, true)})
                      </div>
                    </div>
                  )}

                  <div className="App-card-row">
                    <div className="label">Wallet</div>
                    <div>
                      {formatKeyAmount(tokenInfo, "balance", tokenInfo.decimals, 2, true)} {tokenInfo.symbol} ($
                      {formatAmount(balanceUsd, USD_DECIMALS, 2, true)})
                    </div>
                  </div>
                  <div className="App-card-row">
                    <div className="label">
                      {tokenFeeBps ? (
                        "Fees"
                      ) : (
                        <Tooltip
                          handle={`Fees`}
                          renderContent={() => `Please enter an amount to see fee percentages`}
                        />
                      )}
                    </div>
                    <div>{renderFees()}</div>
                  </div>
                  <div className="App-card-divider"></div>
                  <div className="App-card-options">
                    {isBuying && (
                      <button className="App-button-option App-card-option text-uppercase" onClick={() => selectToken(token)}>
                        +Liq. with {token.symbol}
                      </button>
                    )}
                    {!isBuying && (
                      <button className="App-button-option App-card-option text-uppercase" onClick={() => selectToken(token)}>
                        -liq. for {token.symbol}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {/* </div> */}
      </div>
    </div>
  );
}
